import type { Country } from '../../../__generated__/ts-gql/@schema';

import { LoadPendoService } from './loadPendoService';

export type PendoVisitorIdentity = {
  userId: string;
  email: string | null;
  country: Country | null;
  accountId: string | null;
};

const STAFF_EMAIL_DOMAIN = '@reckon.com';

/**
 * Pendo `region` for every member of the GraphQL `Country` enum.
 *
 * Platform shares one Pendo subscription with portal, and portal reports the
 * ISO-2 country straight into `region` (portal-frontend `js/app.js` writes the
 * token's `country` to `localStorage.region`, which `js/lib/utils.js` reads).
 * `NZ` and `GB` are already ISO-2 and map to themselves; `AUSTRALIA` is the one
 * outlier, folded onto `AU` so Australian visitors don't split across two
 * `region` values in shared segments.
 *
 * Keyed by `Country` rather than `string` so adding a region to the enum fails
 * the build here instead of silently reaching Pendo un-normalised.
 */
const PENDO_REGIONS: Record<Country, string> = {
  AUSTRALIA: 'AU',
  AU: 'AU',
  NZ: 'NZ',
  GB: 'GB',
};

let identifyStarted = false;
let identifiedVisitorId: string | undefined;
let identifiedAccountId: string | null = null;
let markIdentified: (() => void) | undefined;

/**
 * Resolves once the agent reports ready for a known visitor — not merely once
 * `pendo.initialize` has been called, since `isReady()` stays false while the
 * agent finishes booting and any event fired in that window is dropped.
 *
 * It deliberately never resolves for an unidentified user: platform shares one
 * Pendo subscription with portal (same API key), so initialising without a
 * visitor id mints an anonymous cookie visitor that can never be joined back
 * to the real user.
 */
const identified = new Promise<void>((resolve) => {
  markIdentified = resolve;
});

/**
 * Boots the Pendo agent for the authenticated user, mirroring the visitor and
 * account shape portal sends (see portal-frontend `js/lib/utils.js`).
 *
 * Safe to call repeatedly: only the first call gets through, because
 * `pendo.initialize` throws if it runs a second time.
 */
export function identifyPendoVisitor(identity: PendoVisitorIdentity) {
  if (identifyStarted) {
    return;
  }
  identifyStarted = true;
  identifiedVisitorId = identity.userId;
  identifiedAccountId = identity.accountId;

  const email = identity.email;
  const country = identity.country;
  const accountId = identity.accountId;
  // Domains are case-insensitive, so the test is lowercased while the original
  // address is what reaches Pendo — `user@RECKON.COM` is staff, and is reported
  // as the user actually typed it.
  const staffEmail =
    email && email.toLowerCase().endsWith(STAFF_EMAIL_DOMAIN)
      ? email
      : undefined;
  // reckon-graphql types this field as a plain string and passes the portal
  // token's value through untouched (`AuthenticatedUserMapper`), so the enum is
  // a schema-level claim, not a runtime guarantee. An unrecognised value is
  // reported as sent rather than dropping the visitor's region altogether.
  const region = country ? PENDO_REGIONS[country] ?? country : '';

  LoadPendoService.onReady((pendo) => {
    try {
      pendo.initialize({
        visitor: {
          id: identity.userId,
          // Staff addresses are the only ones Pendo gets — a customer's email
          // is not analytics data. Anchored to the end of the address, unlike
          // portal's substring test, so `@reckon.com.<anything>` isn't staff.
          ...(staffEmail ? { email: staffEmail } : {}),
          staff: !!staffEmail,
          region,
        },
        ...(accountId ? { account: { id: accountId } } : {}),
        events: {
          ready: () => {
            markIdentified?.();
          },
        },
      });
    } catch (error) {
      console.warn('[Pendo] Failed to initialize', error);
      // Release the queue even though identification failed. Nothing can reach
      // Pendo now, but leaving the gate pending would park a callback per
      // caller on a promise that can never settle; `trackPlatformPendoEvent`
      // still re-checks `isReady()` before it sends anything.
      markIdentified?.();
    }
  });
}

export function identifyPendoAccount(accountId: string) {
  const visitorId = identifiedVisitorId;
  if (!visitorId || accountId === identifiedAccountId) {
    return;
  }
  identifiedAccountId = accountId;

  onPendoIdentified(() => {
    LoadPendoService.onReady((pendo) => {
      try {
        pendo.identify({
          visitor: { id: visitorId },
          account: { id: accountId },
        });
      } catch (error) {
        console.warn('[Pendo] Failed to identify account', error);
        if (identifiedAccountId === accountId) {
          identifiedAccountId = null;
        }
      }
    });
  });
}

export function notifyPendoPageLoad() {
  onPendoIdentified(() => {
    LoadPendoService.onReady((pendo) => {
      pendo.pageLoad();
    });
  });
}

/**
 * Queues work until the visitor has been identified and the agent is ready.
 * Events fired before that are dropped by the agent's readiness guard, so every
 * track call has to go through here rather than through
 * `LoadPendoService.onReady`, which only waits for the script to download.
 */
export function onPendoIdentified(task: () => void) {
  return identified.then(task);
}
